import React, { FC, useMemo } from 'react';

import type { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Button, Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import PaginationWrapper from '@rover/react-lib/src/components/lists/Carousel/PaginationWrapper';
import SnappingCarousel from '@rover/react-lib/src/components/lists/Carousel/SnappingCarousel';
import type { GroomingSearchResult } from '@rover/rsdk/src/apiClient/latest/model/groomingSearchResult';
import { SearchUIContextProvider } from '@rover/shared/js/providers/SearchUIContextProvider';

import { PageSectionId, useSectionRef } from '../../../hooks/useSectionRef';

import GroomingCarouselCard from './GroomingCarouselCard';

const CTA_WIDTH = '335px';

// The SEO page teases a handful of groomers; the "View more groomers" CTA leads to the
// full search. Keep the carousel short.
const MAX_RESULTS = 7;

type Props = {
  heading: string;
  searchResults: GroomingSearchResult[];
  otherGroomers: GroomingSearchResult[];
  searchPageUrl: string;
  currencyCode?: string;
  locale?: Locale;
};

const GroomingSeoLocationResults: FC<Props> = ({
  heading,
  searchResults,
  otherGroomers,
  searchPageUrl,
  currencyCode,
  locale,
}) => {
  const sectionRef = useSectionRef(PageSectionId.CarouselSearchResults, { threshold: 0.6 });

  // Backfill with the wider-radius groomers so a thin local market still fills the carousel.
  const cards = useMemo(
    () =>
      [...searchResults, ...otherGroomers]
        .slice(0, MAX_RESULTS)
        .map((result, index) => (
          <GroomingCarouselCard
            key={result.groomerWebUrl}
            searchResult={result}
            index={index}
            section={
              index < searchResults.length ? 'Recommended-professionals' : 'Other-professionals'
            }
            currencyCode={currencyCode}
            locale={locale}
          />
        )),
    [searchResults, otherGroomers, currencyCode, locale]
  );

  if (cards.length === 0) return null;

  return (
    <Section mb={['16x', '12x']}>
      <SearchUIContextProvider>
        <Flex htmlRef={sectionRef} gap="6x" flexDirection="column" alignItems="center">
          <PaginationWrapper heading={heading} headingVariant="heading" headingSize="400">
            <SnappingCarousel>{cards}</SnappingCarousel>
          </PaginationWrapper>
          <Button width="100%" maxWidth={[CTA_WIDTH]} href={searchPageUrl}>
            {/* DEV-156190: Translate */}
            View more groomers
          </Button>
        </Flex>
      </SearchUIContextProvider>
    </Section>
  );
};

export default GroomingSeoLocationResults;
